/**
 * Quick guest (Rigs' job picker): a walk-in with no job. One press opens this
 * month's "Studio walk-ins" job, which the server makes the first time it is
 * needed (openStudioWalkins; functions/src/services/studioWalkins.ts and
 * docs/ingest/20-quick-guest.md). From there it is the rig as for any guest:
 * the person registers on their own phone (the Registration QR on the rig) or
 * at the station (Register), with the job's consent statement, then is armed
 * and captured; processing and delivery are the usual ones.
 *
 * Not for an event account (the server refuses it; the picker does not show
 * it). The server also refuses scanners, wranglers and job-scoped logins, and
 * says so; the words are shown as they come.
 */
import React, { useState } from "react";
import { rigv2 } from "./rigv2Callables.js";

export const QUICK_GUEST_HINT = "A walk-in with no job? They go under this month's Studio walk-ins job.";

/**
 * What the picker does with the server's answer: open the job, or stay and
 * say why not. { open: boolean, note: string | null }. Pure, for the test.
 */
export function quickGuestOutcome(result) {
  const name = result?.displayName || result?.jobId || "The walk-ins job";
  if (!result?.jobId) return { open: false, note: "The walk-ins job could not be opened. Try again." };
  if (result.frozen) {
    return { open: false, note: `Captures on ${name} are closed. Ask an admin.` };
  }
  if (!result.consentReady) {
    return {
      open: false,
      note: `${name} is ready, but it has no consent statement yet, so nobody can register on it. An admin adds the walk-in consent statement on its Settings tab and opens registration.`,
    };
  }
  return { open: true, note: null };
}

/**
 * One press: ask the server, then open the job or say why not. Resolves to
 * { note: { text, jobId } | null, error: string | null }; never throws.
 * Exported for the test (no DOM in the unit tests).
 */
export async function runQuickGuest({ open, onOpen }) {
  try {
    const result = await open();
    const outcome = quickGuestOutcome(result);
    if (outcome.open) {
      onOpen(result.jobId);
      return { note: null, error: null };
    }
    return { note: { text: outcome.note, jobId: result?.jobId ?? null }, error: null };
  } catch (e) {
    return { note: null, error: e?.message ?? String(e) };
  }
}

export default function QuickGuest({ onOpen, open = rigv2.openStudioWalkins }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);       // why the job did not open, with its id to open anyway
  const [error, setError] = useState(null);

  const press = async () => {
    if (busy) return;
    setBusy(true); setError(null); setNote(null);
    const r = await runQuickGuest({ open, onOpen });
    setNote(r.note); setError(r.error); setBusy(false);
  };

  return (
    <div className="rigv2-quickguest">
      <button type="button" className="rigv2-btn primary rigv2-quickguest-btn" onClick={press} disabled={busy}>
        {busy ? "Opening…" : "Quick guest"}
      </button>
      <span className="rigv2-quickguest-hint">{QUICK_GUEST_HINT}</span>
      {note && (
        <div className="rigv2-banner" role="status">
          {note.text}
          {note.jobId && (
            <button type="button" className="rigv2-linkbtn" style={{ marginLeft: 8 }} onClick={() => onOpen(note.jobId)}>Open it anyway</button>
          )}
        </div>
      )}
      {error && <div className="rigv2-banner error" role="alert">{error}</div>}
    </div>
  );
}
